import React, { useCallback, useState } from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import {
  useCurrentUserContext,
  useGlobalizationContext,
  useTranslation,
  useGetContentType,
  useGetAllFields,
  useGetDocumentVersions,
  useGetDocumentVersionMetadata
} from '@cs-cms/state-management';
import { DocumentVersionResponse } from '@cs-cms/types';
import { useUIConfig } from '../../UiProvider';
import { buildDocumentVersionDownloadUrl } from './documentVersionViewerHelpers';
import {
  DocumentVersionViewerHeader,
  VersionDetailsPanel,
  VersionListPanel
} from './DocumentVersionViewerPanels';

interface DocumentVersionViewerProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  documentName: string;
  contentType: string;
}

export const DocumentVersionViewer: React.FC<DocumentVersionViewerProps> = ({
  isOpen,
  onClose,
  documentId,
  documentName,
  contentType
}) => {
  const { selectedIndex } = useCurrentUserContext();
  const [selectedVersion, setSelectedVersion] = useState<DocumentVersionResponse | null>(null);
  const { selectedLocale } = useGlobalizationContext();
  const { apiUrl } = useUIConfig();
  const { translate } = useTranslation(selectedLocale);

  const { data: contentTypeData } = useGetContentType(contentType, selectedLocale);
  const { data: allFields } = useGetAllFields(selectedLocale, selectedIndex?.id);

  const {
    data: versions = [],
    isLoading,
    error
  } = useGetDocumentVersions(
    {
      documentId,
      ctName: contentType,
      indexId: selectedIndex?.id ?? -1
    },
    {
      enabled: isOpen && selectedIndex?.id != null
    }
  );

  const { data: versionMetadata, isLoading: isLoadingMetadata } = useGetDocumentVersionMetadata(
    {
      documentId,
      ctName: contentType,
      indexId: selectedIndex?.id ?? -1,
      versionId: selectedVersion?.versionId || ''
    },
    { enabled: isOpen && !!selectedVersion?.versionId }
  );

  const getDownloadUrl = useCallback(
    (version: DocumentVersionResponse): string | undefined =>
      buildDocumentVersionDownloadUrl(apiUrl, contentType, documentId, version, selectedIndex?.id),
    [apiUrl, contentType, documentId, selectedIndex?.id]
  );

  const handleClose = useCallback(() => {
    setSelectedVersion(null);
    onClose();
  }, [onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog
      onClose={handleClose}
      className="documentVersionViewerWindow"
      closeIcon={true}
      title={translate('grid.versionViewer.dialogTitle')}
    >
      <DocumentVersionViewerHeader documentName={documentName} documentId={documentId} translate={translate} />
      <div className="mainContent">
        <VersionListPanel
          versions={versions}
          isLoading={isLoading}
          error={error}
          selectedVersion={selectedVersion}
          isLoadingMetadata={isLoadingMetadata}
          documentName={documentName}
          getDownloadUrl={getDownloadUrl}
          onSelectVersion={setSelectedVersion}
          translate={translate}
        />
        <VersionDetailsPanel
          selectedVersion={selectedVersion}
          versionMetadata={versionMetadata?.metadata}
          isLoadingMetadata={isLoadingMetadata}
          documentName={documentName}
          allFields={allFields}
          contentTypeData={contentTypeData}
          getDownloadUrl={getDownloadUrl}
          translate={translate}
        />
      </div>
    </Dialog>
  );
};
